import { AppSettings, AsamcoAuthService, ServerSetting } from '@asamco/base';
import * as signalR from '@microsoft/signalr';
import { combineLatest, from, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { SignalrHook } from './signalr-hook';

export abstract class AsamcoSignalrBase {
	/** The name of the hub to subscribe on */
	protected abstract hubName: string;

	/** Events to register */
	protected abstract events: SignalrHook[];

	/** The logging level */
	protected logLevel: signalR.LogLevel = signalR.LogLevel.Error;

	hubConnection: signalR.HubConnection;

	/**
	 *Creates an instance of AsamcoSignalrBase.
	 * @param {AsamcoAuthService} auth
	 * @param {AppSettings} app
	 * @memberof AsamcoSignalrBase
	 */
	protected constructor(protected auth: AsamcoAuthService, protected app: AppSettings) {}

	/**
	 * Starts the service
	 */
	protected startService(): Observable<void> {
		// return if signalR is disabled
		if ((this.app.settings.isSignalREnabled || false) === false) return of(null);

		// Subscribe to isLoggedIn$ to be notified when all backends are logged in or out to
		// If logged in, connect to all backends
		// if logged out, remove all connections

		return combineLatest([this.auth.isLoggedIn$, this.auth.authTokenChanged$]).pipe(
			map(([isLoggedIn, authTokenChanged]) => ({ isLoggedIn, authTokenChanged })),
			switchMap(res => {
				if (res.isLoggedIn) {
					return from(this.auth.getPrimaryTokenAsync()).pipe(switchMap(pt => this.createConnection(this.auth.userSession.backend, pt)));
				} else {
					return from(this.stopService());
				}
			})
		);
	}

	/**
	 * Stops the service
	 */
	protected stopService(): Promise<void> {
		return this.hubConnection?.stop() ?? Promise.resolve();
	}

	/**
	 * Create a signalR connection to the backend
	 * @param backend
	 */
	private createConnection(backend: ServerSetting, primaryToken): Promise<void> {
		// Get the actual base of the base url
		const baseUrl = backend.baseUrl.replace(/\/api\/?/, '');

		if (this.hubConnection != null && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
			this.hubConnection.stop();
		}

		this.hubConnection = new signalR.HubConnectionBuilder()
			.withUrl(baseUrl + '/hubs/' + this.hubName, {
				accessTokenFactory: () => primaryToken,
			})
			.withAutomaticReconnect()
			.configureLogging(this.logLevel)
			.build();

		return this.hubConnection
			.start()
			.then(_ => {
				this.registerEvents(this.hubConnection);
			})
			.catch(err => console.error('Error while starting connection: ' + err)); // NOSONAR - warn the developer
	}

	/**
	 * Register hub events
	 */
	registerEvents(connection: signalR.HubConnection) {
		this.events.forEach(ev => {
			connection.on(ev.methodName, ev.newMethod);
		});
	}
}
